import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { villas, OWNER } from "@/data/villas";
import SEO from "@/components/SEO";
import {
  Palmtree,
  Waves,
  UtensilsCrossed,
  Car,
  Flower2,
  BellRing,
  BedDouble,
  Sparkles,
  Plane,
  Compass,
  Shirt,
  Check,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";

const HERO = villas.hasna.gallery[0]; // Piscine 28m
const AMBIANCE = villas.hasna.gallery[1];

// Pricing grid (couple / total for the duration)
const FORMULAS = [
  {
    key: "essentielle",
    label: "Essentielle",
    accent: false,
    prices: { week: 1900, fortnight: 3650, month: 6520 },
    perPerson: { week: 950, fortnight: 1825, month: 3260 },
    extras: [
      { icon: UtensilsCrossed, text: "Petit-déjeuner quotidien" },
    ],
  },
  {
    key: "confort",
    label: "Confort",
    accent: true,
    prices: { week: 2330, fortnight: 4570, month: 8350 },
    perPerson: { week: 1165, fortnight: 2285, month: 4175 },
    extras: [
      { icon: UtensilsCrossed, text: "Demi-pension" },
      { icon: Car, text: "1 trajet A/R centre-ville / jour" },
      { icon: Flower2, text: "1 massage / pers. / semaine" },
    ],
  },
  {
    key: "premium",
    label: "Premium",
    accent: false,
    prices: { week: 2660, fortnight: 5280, month: 9770 },
    perPerson: { week: 1330, fortnight: 2640, month: 4885 },
    extras: [
      { icon: UtensilsCrossed, text: "Pension complète" },
      { icon: Car, text: "Chauffeur privé à disposition" },
      { icon: Flower2, text: "2 massages / pers. / semaine" },
    ],
  },
];

const INCLUDED = [
  { icon: BedDouble, key: "suite" },
  { icon: Sparkles, key: "housekeeping" },
  { icon: Plane, key: "shuttle" },
  { icon: Compass, key: "advice" },
  { icon: Shirt, key: "laundry" },
  { icon: Waves, key: "pool" },
];

const BADGES = [
  { icon: Palmtree, key: "palm" },
  { icon: Waves, key: "heated" },
  { icon: UtensilsCrossed, key: "pension" },
  { icon: Flower2, key: "massage" },
  { icon: BellRing, key: "concierge" },
];

export default function Seniors() {
  const { t } = useLang();
  const s = t.seniors;

  return (
    <>
      <SEO pageKey="seniors" />
      {/* HERO */}
      <section className="relative -mt-20 h-[90svh] overflow-hidden" data-testid="seniors-hero">
        <img src={HERO} alt="Séjours Sérénité — Villa Hasna" className="absolute inset-0 h-full w-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/70" />
        <div className="relative z-10 h-full dh-container flex flex-col justify-end pb-24 text-white">
          <div className="fade-up max-w-3xl">
            <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">
              Dar Hasna Collection · Villa Hasna
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] mt-4">
              {s.title}
              <span className="block italic text-[color:hsl(var(--dh-ocre))]">{s.titleItalic}</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 italic max-w-xl">{s.tagline}</p>
          </div>
        </div>
      </section>

      {/* BADGES row */}
      <section className="dh-container py-14">
        <div className="flex flex-wrap justify-center gap-3">
          {BADGES.map(({ icon: Icon, key }) => (
            <div key={key} data-testid={`seniors-badge-${key}`} className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-[color:hsl(var(--dh-ocre)/0.3)] rounded-full text-sm">
              <Icon className="h-4 w-4 text-[color:hsl(var(--dh-terracotta))]" />
              <span>{s.badges[key]}</span>
            </div>
          ))}
        </div>
      </section>

      {/* PITCH */}
      <section className="dh-container pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="dh-overline">{s.pitchOverline}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">{s.pitchTitle}</h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[color:hsl(var(--dh-muted))] text-base leading-relaxed">
            <p>{s.pitch1}</p>
            <p>{s.pitch2}</p>
          </div>
        </div>
      </section>

      {/* AMBIANCE band */}
      <section className="relative h-[60vh] overflow-hidden">
        <img src={AMBIANCE} alt="Villa Hasna" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        <div className="relative z-10 h-full dh-container flex items-end pb-12 text-white">
          <div className="max-w-md">
            <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">{s.homeOverline}</div>
            <div className="font-serif text-3xl md:text-4xl mt-3">{s.homeTitle}</div>
          </div>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="dh-container py-20 md:py-24">
        <div className="max-w-2xl mb-12">
          <div className="dh-overline">{s.includedOverline}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">{s.includedTitle}</h2>
          <p className="mt-4 text-[color:hsl(var(--dh-muted))]">{s.includedIntro}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {INCLUDED.map(({ icon: Icon, key }) => (
            <div key={key} data-testid={`seniors-inc-${key}`} className="dh-card p-6 flex gap-4">
              <Icon className="h-6 w-6 text-[color:hsl(var(--dh-terracotta))] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-serif text-xl">{s.included[key].title}</div>
                <div className="text-sm text-[color:hsl(var(--dh-muted))] mt-1">{s.included[key].desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FORMULAS */}
      <section id="formulas" className="bg-white border-y border-[color:hsl(var(--dh-ocre)/0.15)]">
        <div className="dh-container py-20 md:py-28">
          <div className="max-w-2xl mb-14">
            <div className="dh-overline">{s.formulasOverline}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">{s.formulasTitle}</h2>
            <p className="mt-4 text-[color:hsl(var(--dh-muted))]">{s.formulasIntro}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {FORMULAS.map((f, i) => (
              <div
                key={f.key}
                data-testid={`seniors-formula-${f.key}`}
                className={`p-8 flex flex-col ${
                  f.accent
                    ? "bg-[color:hsl(var(--dh-palm))] text-[color:hsl(var(--dh-sand))] border border-[color:hsl(var(--dh-palm))]"
                    : "dh-card"
                }`}
              >
                <div className={`dh-overline ${f.accent ? "text-[color:hsl(var(--dh-ocre))]" : "text-[color:hsl(var(--dh-terracotta))]"}`}>
                  Formule {String.fromCharCode(8544 + i)}
                </div>
                <div className="font-serif text-4xl mt-2">{f.label}</div>

                <div className="mt-8">
                  <div className={`text-xs uppercase tracking-widest ${f.accent ? "opacity-70" : "text-[color:hsl(var(--dh-muted))]"}`}>{s.dur.week}</div>
                  <div className="font-serif text-5xl mt-1">
                    {f.prices.week.toLocaleString("fr-FR")} <span className="text-2xl">€</span>
                  </div>
                  <div className={`text-xs mt-1 ${f.accent ? "opacity-70" : "text-[color:hsl(var(--dh-muted))]"}`}>
                    {s.perCouple} · {f.perPerson.week} € / pers.
                  </div>
                </div>

                <div className={`mt-6 pt-6 border-t ${f.accent ? "border-white/15" : "border-[color:hsl(var(--dh-ocre)/0.2)]"} space-y-2 text-sm`}>
                  <div className="flex justify-between">
                    <span className={f.accent ? "opacity-80" : "text-[color:hsl(var(--dh-muted))]"}>{s.dur.fortnight}</span>
                    <span className="font-medium">{f.prices.fortnight.toLocaleString("fr-FR")} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={f.accent ? "opacity-80" : "text-[color:hsl(var(--dh-muted))]"}>{s.dur.month}</span>
                    <span className="font-medium">{f.prices.month.toLocaleString("fr-FR")} €</span>
                  </div>
                </div>

                <div className={`mt-8 pt-6 border-t ${f.accent ? "border-white/15" : "border-[color:hsl(var(--dh-ocre)/0.2)]"} space-y-3 flex-1`}>
                  <div className={`dh-overline ${f.accent ? "text-[color:hsl(var(--dh-ocre))]" : ""}`}>{s.plusIncluded}</div>
                  {f.extras.map((ex, j) => (
                    <div key={j} className="flex items-baseline gap-2 text-sm">
                      <Check className={`h-4 w-4 flex-shrink-0 ${f.accent ? "text-[color:hsl(var(--dh-ocre))]" : "text-[color:hsl(var(--dh-terracotta))]"}`} />
                      <span>{ex.text}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  data-testid={`seniors-cta-${f.key}`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 py-3 text-xs uppercase tracking-widest transition-colors ${
                    f.accent
                      ? "bg-[color:hsl(var(--dh-terracotta))] text-white hover:bg-[color:hsl(var(--dh-terracotta-dark))]"
                      : "border border-[color:hsl(var(--dh-terracotta))] text-[color:hsl(var(--dh-terracotta))] hover:bg-[color:hsl(var(--dh-terracotta))] hover:text-white"
                  }`}
                >
                  {s.chooseFormula} <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-8 text-xs text-[color:hsl(var(--dh-muted))] max-w-2xl">
            <p>{s.terms}</p>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-[color:hsl(var(--dh-palm))] text-[color:hsl(var(--dh-sand))]">
        <div className="dh-container py-20 text-center">
          <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">{s.moreOverline}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">{s.moreTitle}</h2>
          <p className="mt-4 opacity-85 max-w-xl mx-auto">{s.moreSubtitle}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href={OWNER.waLink} target="_blank" rel="noreferrer" data-testid="seniors-wa" className="inline-flex items-center gap-2 bg-[color:hsl(var(--dh-terracotta))] text-white px-6 py-3 text-sm uppercase tracking-widest hover:bg-[color:hsl(var(--dh-terracotta-dark))] transition-colors">
              <Phone className="h-4 w-4" />
              +212 (0)7 18 11 54 19
            </a>
            <a href={`mailto:${OWNER.email}?subject=Séjour Sérénité Seniors`} data-testid="seniors-mail" className="inline-flex items-center gap-2 border border-[color:hsl(var(--dh-ocre))] px-6 py-3 text-sm uppercase tracking-widest hover:bg-[color:hsl(var(--dh-ocre)/0.15)] transition-colors">
              <Mail className="h-4 w-4" />
              {OWNER.email}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
